import { FC, memo } from 'react';
import { useAppSelector, useAppDispatch } from '@doda/pc/src/redux/hooks';
import { tl } from '@doda/common/src/utils/adobeAnalyticsUtil';
import { ImpressionAnalytics } from '@doda/common/src/components/Analytics/ImpressionAnalytics';
import { VisibleAnalytics } from '@doda/common/src/components/Analytics/VisibleAnalytics';
import { canonicalUrlSelector } from '@doda/common/src/redux/features/canonical';
import { plpLinkListDataIndexSelector } from '@doda/common/src/redux/features/plpLinkList';
import { PLPLINKLIST } from '@doda/common/src/constants/seo/plpLinks';
import { TextLinkList } from '@doda/io-react/common';
import {
  PLPLINKLIST_JOBSEARCHLIST_UPPER,
  PLPLINKLIST_JOBSEARCHLIST_UNDER,
} from '@doda/common/src/constants/seo';
import styles from './index.module.scss';

/**
 * 表示位置
 */
interface Props {
  position: number;
}

/**
 * listDataの作成
 * @param nowPageSkipUrl 現在のページ（スキップ対象）
 * @param plpItemLinks マスタ値（jsonデータ）
 * @param position どちらの表示位置か（上部か下部か）
 * @returns data 表示対象のリンク群（urlとtextタイトル）
 */
export const getPlpLinkListData = (
  nowPageSkipUrl: string,
  plpItemLinks: object,
  position: number
) => {
  return Object.keys(plpItemLinks)
    .filter((key) => !nowPageSkipUrl.includes(key))
    .map((key) => {
      return {
        href: key,
        text: plpItemLinks[key as keyof typeof plpItemLinks],
        onClick: measurePlpLinkListClick(position),
      };
    });
};

/**
 * 検索結果一覧 PLP用の内部リンクリスト（関連の業種・職種から探す）
 */
export const PlpLinkList: FC<Props> = memo(({ position }) => {
  const dispatch = useAppDispatch();
  const canonicalUrl = useAppSelector(canonicalUrlSelector) || '';
  const nowPageSkipUrl = canonicalUrl
    ? canonicalUrl.replace(/\/-page__[0-9]+/, '')
    : '';
  const plpIndex = useAppSelector(plpLinkListDataIndexSelector);
  const isShowPlpLink = !(typeof plpIndex === 'undefined' || plpIndex === -1);
  const plpItemLinks = isShowPlpLink
    ? PLPLINKLIST[plpIndex].centralizeLinks
    : {};
  const linkList = getPlpLinkListData(nowPageSkipUrl, plpItemLinks, position);

  return (
    isShowPlpLink && (
      <VisibleAnalytics
        onVisible={() => dispatch(measurePlpLinkListVisible(position))}
      >
        <div className={styles.jobSearchList_plpLinkListArea}>
          <TextLinkList
            columnCount="1"
            arrow="blue"
            titleText="関連の業種・職種から探す"
            rounded
            shadow="blue"
            listData={linkList}
          />
          <ImpressionAnalytics
            adobe={() =>
              position === PLPLINKLIST_JOBSEARCHLIST_UNDER &&
              measurePlpLinkListImpression()
            }
          />
        </div>
      </VisibleAnalytics>
    )
  );
});
PlpLinkList.displayName = 'PlpLinkList';

/**
 * PLP内部リンクコンテンツの計測（インプレッション）
 */
const measurePlpLinkListImpression = () => {
  tl('addlink_imp', {});
};

/**
 * PLP内部リンクコンテンツの計測（表示）
 * @param position
 */
const measurePlpLinkListVisible = (position: number) => () => {
  switch (position) {
    case PLPLINKLIST_JOBSEARCHLIST_UPPER:
      tl('addlink_top_visible', {});
      break;
    case PLPLINKLIST_JOBSEARCHLIST_UNDER:
      tl('addlink_bottom_visible', {});
      break;
    default:
      break;
  }
};

/**
 * PLP内部リンクコンテンツの計測（クリック時）
 * @param position
 */
const measurePlpLinkListClick = (position: number) => () => {
  switch (position) {
    case PLPLINKLIST_JOBSEARCHLIST_UPPER:
      tl('addlink_top_click', {});
      break;
    case PLPLINKLIST_JOBSEARCHLIST_UNDER:
      tl('addlink_bottom_click', {});
      break;
    default:
      break;
  }
};
